/**
 * Toast Container
 * 
 * Container component for managing and displaying toast notifications.
 * Handles positioning, stacking, and lifecycle management.
 * 
 * @component
 */

import React, { useState, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import Toast from './Toast';

const ToastContext = createContext(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast = { id, ...toast };
    
    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback(
    (title, message, options = {}) => {
      return addToast({ variant: 'success', title, message, ...options });
    },
    [addToast]
  );

  const showError = useCallback(
    (title, message, options = {}) => {
      return addToast({ variant: 'error', title, message, duration: 7000, ...options });
    },
    [addToast]
  );

  const showWarning = useCallback(
    (title, message, options = {}) => {
      return addToast({ variant: 'warning', title, message, ...options });
    },
    [addToast]
  );

  const showInfo = useCallback(
    (title, message, options = {}) => {
      return addToast({ variant: 'info', title, message, ...options });
    },
    [addToast]
  );

  const contextValue = {
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Toast Container */}
      <div
        className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} onClose={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ToastProvider;
